import { Link } from "react-router-dom";
import { Shield, Clock, Wallet, Scale, Building2, Wrench, Zap, ArrowRight, Star, Users, TrendingUp, Lock, Heart, Sparkles } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import chargeflipLogo from "@/assets/chargeflip-logo.png";
import wallboxHero from "@/assets/wallbox-car.png";
import wallboxCloseup from "@/assets/wallbox-closeup.jpeg";
import ParallaxImage from "@/components/ParallaxImage";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: "easeOut" as const },
  }),
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const faqItems = [
  {
    icon: Scale,
    question: "Ist das überhaupt legal?",
    answer: "Absolut. Du vermietest den Zugang zu deiner Ladeinfrastruktur — kein Stromverkauf, kein Eichrecht, keine Konzession. Fällt unter §21 EStG (Vermietung & Verpachtung). Wir haben das mit Anwälten geprüft.",
  },
  {
    icon: Building2,
    question: "Brauche ich ein Gewerbe?",
    answer: "Nein! Es sind Vermietungseinkünfte. Kein Gewerbe, kein Finanzamt-Stress. Einfach in Anlage V deiner Steuererklärung eintragen — fertig.",
  },
  {
    icon: Wallet,
    question: "Was ist mit Steuern?",
    answer: "Bis 1.000 € pro Jahr komplett steuerfrei. Das heißt: Wenn deine Wallbox dir 80 € im Monat einbringt, zahlst du keinen Cent an Steuern. Null.",
  },
  {
    icon: Shield,
    question: "Was wenn etwas kaputt geht?",
    answer: "Jede Buchung ist über unsere Plattform-Haftpflicht versichert. Du trägst kein Risiko — wir stehen dafür gerade.",
  },
  {
    icon: Wrench,
    question: "Brauche ich spezielle Hardware?",
    answer: "Nein. Jede handelsübliche Wallbox reicht. Typ 2, Schuko, CCS — egal. Kein geeichter Zähler nötig, wir rechnen zeitbasiert ab.",
  },
];

const steps = [
  {
    icon: Zap,
    title: "Anmelden",
    desc: "Kostenlos in 2 Minuten. Adresse eingeben, fertig. Wir kümmern uns um den Rest.",
    num: "01",
  },
  {
    icon: Clock,
    title: "Zeiten wählen",
    desc: "Du entscheidest, wann geladen werden darf. Volle Kontrolle, null Verpflichtung.",
    num: "02",
  },
  {
    icon: Wallet,
    title: "Geld erhalten",
    desc: "Jede Buchung = automatische Einnahme. Direkt auf dein Konto, ohne dass du etwas tun musst.",
    num: "03",
  },
];

const trustPoints = [
  { icon: Lock, text: "Verschlüsselte Bezahlung" },
  { icon: Shield, text: "Versicherte Ladevorgänge" },
  { icon: Star, text: "Geprüfte Ladepunkte" },
];

const Index = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  const { data: hostCount } = useQuery({
    queryKey: ["host_count"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_host_count");
      if (error) return 0;
      return Number(data) || 0;
    },
  });

  const { data: driverCount } = useQuery({
    queryKey: ["driver_waitlist_count"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_driver_waitlist_count");
      if (error) return 0;
      return Number(data) || 0;
    },
  });

  const totalCommunity = (hostCount ?? 0) + (driverCount ?? 0);

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b border-border/50 bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/80">
        <div className="container flex h-14 items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={chargeflipLogo} alt="chargeflip Logo" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/driver-waitlist" className="hidden text-sm text-muted-foreground transition-colors hover:text-foreground sm:block">
              Ladestation finden
            </Link>
            <Link to="/host-signup">
              <Button size="sm" className="gap-1.5 rounded-full px-5 shadow-sm shadow-primary/20">
                <Sparkles className="h-3.5 w-3.5" />
                Jetzt starten
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main>
        {/* ═══════════════════════════ HERO ═══════════════════════════ */}
        <section ref={heroRef} className="relative overflow-hidden pb-10 pt-12 md:pb-20 md:pt-16">
          {/* Subtle radial glow */}
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute left-1/2 top-0 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/3 rounded-full bg-primary/[0.07] blur-3xl" />
          </div>

          <motion.div style={{ y: heroY, opacity: heroOpacity }} className="container flex flex-col items-center text-center">
            {/* Urgency badge */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={0}
              className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 text-xs font-medium text-primary sm:text-sm"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
              </span>
              {totalCommunity > 0
                ? `${totalCommunity} Personen sind bereits dabei`
                : "Exklusiv — Limitierte Plätze im Early Access"}
            </motion.div>

            <motion.h1
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={1}
              className="max-w-2xl text-[1.75rem] font-bold leading-[1.1] tracking-tight sm:text-4xl md:text-5xl"
              style={{ textWrap: "balance" } as React.CSSProperties}
            >
              Deine Wallbox.{" "}
              <span className="gradient-text">Dein passives Einkommen.</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={2}
              className="mt-5 max-w-lg text-[0.95rem] leading-relaxed text-muted-foreground sm:text-lg"
              style={{ textWrap: "pretty" } as React.CSSProperties}
            >
              Verdiene bis zu <strong className="text-foreground">1.000 € steuerfrei</strong> im Jahr — einfach indem deine Wallbox steht, wo sie sowieso steht. Kein Aufwand, kein Gewerbe, kein Risiko.
            </motion.p>

            {/* CTA buttons */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={3}
              className="mt-8 flex flex-col gap-3 sm:flex-row"
            >
              <Link to="/host-signup">
                <Button size="lg" className="gap-2 rounded-full px-8 text-base font-semibold shadow-lg shadow-primary/25 transition-shadow hover:shadow-xl hover:shadow-primary/30">
                  <Wallet className="h-5 w-5" />
                  Kostenlos Platz sichern
                </Button>
              </Link>
              <Link to="/driver-waitlist">
                <Button size="lg" variant="outline" className="gap-2 rounded-full border-border/60 px-8 text-base">
                  Ladestation finden
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </motion.div>

            {/* Trust micro-badges */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={4}
              className="mt-6 flex flex-wrap justify-center gap-4"
            >
              {trustPoints.map((tp) => (
                <span key={tp.text} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <tp.icon className="h-3.5 w-3.5 text-primary/70" />
                  {tp.text}
                </span>
              ))}
            </motion.div>

            {/* Hero image */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={5}
              className="mt-12 w-full max-w-2xl"
            >
              <ParallaxImage
                src={wallboxHero}
                alt="Wallbox an einer Hauswand mit grüner LED-Anzeige"
              />
            </motion.div>
          </motion.div>
        </section>

        {/* ═══════════════════════════ SOCIAL PROOF BAR ═══════════════════════════ */}
        <section className="border-y border-border/50 bg-muted/30">
          <div className="container grid grid-cols-3 gap-4 py-7">
            {[
              { value: hostCount ?? 0, label: "Ladepunkte registriert", icon: Zap },
              { value: driverCount ?? 0, label: "Fahrer warten auf Launch", icon: Users },
              { value: "0 €", label: "Kosten für dich", icon: Heart },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex flex-col items-center text-center"
              >
                <item.icon className="mb-1.5 h-4 w-4 text-primary/60" />
                <span className="text-xl font-bold tabular-nums text-foreground sm:text-2xl md:text-3xl">
                  {typeof item.value === "number" ? item.value : item.value}
                </span>
                <span className="mt-0.5 text-[0.7rem] leading-tight text-muted-foreground sm:text-xs">{item.label}</span>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ═══════════════════════════ WHY NOW? ═══════════════════════════ */}
        <section className="py-16 md:py-24">
          <div className="container max-w-3xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="mb-3 inline-block rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-primary">
                Warum jetzt?
              </span>
              <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Die besten Plätze gehen an die Ersten
              </h2>
              <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-muted-foreground sm:text-base">
                Jeder Stadtteil, jedes Dorf — wir starten dort, wo die Community zuerst wächst.
                Wer früh dabei ist, bekommt die meisten Buchungen und die beste Sichtbarkeit.
              </p>
            </motion.div>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="mt-10 grid gap-4 sm:grid-cols-3"
            >
              {[
                { icon: TrendingUp, title: "Erster in deiner Region", desc: "Keine Konkurrenz — alle E-Fahrer in deiner Gegend kommen zu dir." },
                { icon: Star, title: "Gründer-Status", desc: "Frühe Mitglieder erhalten Sonderkonditionen und Premium-Sichtbarkeit." },
                { icon: Sparkles, title: "Kein Risiko", desc: "Keine Kosten, keine Vertragsbindung. Jederzeit pausieren oder aufhören." },
              ].map((item) => (
                <motion.div
                  key={item.title}
                  variants={fadeUp}
                  className="rounded-2xl border border-border/60 bg-card p-6 text-left shadow-sm transition-all hover:border-primary/20 hover:shadow-md"
                >
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                    <item.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="text-sm font-semibold">{item.title}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{item.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ═══════════════════════════ HOW IT WORKS ═══════════════════════════ */}
        <section className="border-y border-border/50 bg-muted/20 py-16 md:py-24" id="so-funktionierts">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <span className="mb-2 inline-block text-xs font-semibold uppercase tracking-widest text-primary sm:text-sm">
                So einfach geht's
              </span>
              <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                3 Schritte. Kein Aufwand.
              </h2>
              <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
                Melde dich an, lehn dich zurück und verdiene.
              </p>
            </motion.div>

            <div className="mt-12 grid gap-5 md:grid-cols-3">
              {steps.map((s, i) => (
                <motion.div
                  key={s.title}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={i}
                  className="group relative overflow-hidden rounded-2xl border border-border/60 bg-card p-7 shadow-sm transition-all hover:border-primary/20 hover:shadow-md"
                >
                  <span className="absolute right-4 top-4 text-3xl font-black text-primary/[0.07]">
                    {s.num}
                  </span>
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-base font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
                </motion.div>
              ))}
            </div>

            {/* CTA after steps */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-10 flex justify-center"
            >
              <Link to="/host-signup">
                <Button size="lg" className="gap-2 rounded-full px-8 shadow-md shadow-primary/20">
                  Jetzt kostenlos starten
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* ═══════════════════════════ FEATURE HIGHLIGHT + IMAGE ═══════════════════════════ */}
        <section className="py-16 md:py-24">
          <div className="container">
            <div className="grid items-center gap-10 md:grid-cols-2">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <span className="mb-2 inline-block rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-primary">
                  Dein Vorteil
                </span>
                <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                  Geld verdienen,{" "}
                  <span className="gradient-text">ohne einen Finger zu rühren.</span>
                </h2>
                <p className="mt-4 max-w-md leading-relaxed text-muted-foreground">
                  Deine Wallbox steht sowieso in der Garage oder an der Hauswand.
                  Jetzt lässt du sie einfach nebenbei für dich arbeiten — wie ein stiller Mitbewohner, der Miete zahlt.
                </p>

                {/* Benefit chips */}
                <div className="mt-5 flex flex-wrap gap-2">
                  {["Steuerfrei bis 1.000 €", "Kein Gewerbe nötig", "Jederzeit pausierbar", "Versichert"].map((chip) => (
                    <span key={chip} className="rounded-full border border-primary/15 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
                      {chip}
                    </span>
                  ))}
                </div>

                <Link to="/host-signup" className="mt-6 inline-block">
                  <Button className="gap-2 rounded-full px-6 shadow-sm shadow-primary/15">
                    Kostenlos starten
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <ParallaxImage
                  src={wallboxCloseup}
                  alt="Wallbox Nahaufnahme im Sonnenlicht"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════ MONEY CALCULATOR TEASER ═══════════════════════════ */}
        <section className="border-y border-border/50 bg-muted/20 py-16">
          <div className="container max-w-xl text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="rounded-2xl border border-primary/15 bg-card p-8 shadow-sm"
            >
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
                <TrendingUp className="h-7 w-7 text-primary" />
              </div>
              <h2 className="text-xl font-bold sm:text-2xl">
                Bis zu <span className="gradient-text">83 € im Monat</span>
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Bei nur 2–3 Buchungen pro Woche verdienst du bis zu 1.000 € im Jahr — komplett steuerfrei.
                Und das Beste: Du musst dafür <strong className="text-foreground">nichts tun</strong>.
              </p>
              <Link to="/host-signup" className="mt-6 inline-block">
                <Button size="lg" className="rounded-full px-8 shadow-md shadow-primary/20">
                  Platz sichern — kostenlos
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* ═══════════════════════════ VIDEO + TRUST ═══════════════════════════ */}
        <section className="relative overflow-hidden py-20 md:py-28">
          <div className="absolute inset-0 -z-10">
            <video
              autoPlay
              muted
              loop
              playsInline
              className="h-full w-full object-cover opacity-15"
              src="/videos/wallbox-video-1.mp4"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background via-background/85 to-background" />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="container flex flex-col items-center text-center"
          >
            <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <h2 className="text-xl font-bold sm:text-2xl">
              Sicher & geprüft — damit du ruhig schlafen kannst
            </h2>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
              Jeder Ladepunkt wird geprüft. Jede Zahlung verschlüsselt. Jeder Ladevorgang versichert.
              Wir haben an alles gedacht, damit du es nicht musst.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              {trustPoints.map((tp) => (
                <span key={tp.text} className="flex items-center gap-1.5 rounded-full border border-border/50 bg-card/80 px-3 py-1.5 text-xs font-medium text-foreground backdrop-blur-sm">
                  <tp.icon className="h-3.5 w-3.5 text-primary" />
                  {tp.text}
                </span>
              ))}
            </div>
          </motion.div>
        </section>

        {/* ═══════════════════════════ VIDEO SHOWCASE ═══════════════════════════ */}
        <section className="py-14">
          <div className="container max-w-2xl">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="overflow-hidden rounded-2xl shadow-xl ring-1 ring-border/30"
            >
              <video
                autoPlay
                muted
                loop
                playsInline
                className="w-full"
                src="/videos/wallbox-video-2.mp4"
              />
            </motion.div>
          </div>
        </section>

        {/* ═══════════════════════════ FAQ ═══════════════════════════ */}
        <section className="border-t border-border/50 bg-muted/20 py-16 md:py-24" id="faq">
          <div className="container max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <span className="mb-2 inline-block text-xs font-semibold uppercase tracking-widest text-primary">Noch Fragen?</span>
              <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Wir haben die Antworten
              </h2>
              <p className="mx-auto mb-10 mt-3 max-w-md text-sm text-muted-foreground">
                Kein Juristendeutsch, kein Kleingedrucktes — nur ehrliche Antworten.
              </p>
            </motion.div>

            <Accordion type="single" collapsible className="space-y-3">
              {faqItems.map((item, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={i}
                >
                  <AccordionItem
                    value={`faq-${i}`}
                    className="rounded-xl border border-border/60 bg-card px-6 shadow-sm"
                  >
                    <AccordionTrigger className="text-left text-sm font-semibold hover:no-underline gap-3 py-4">
                      <span className="flex items-center gap-3">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                          <item.icon className="h-4 w-4 text-primary" />
                        </span>
                        {item.question}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground pb-4 pl-11">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          </div>
        </section>

        {/* ═══════════════════════════ FINAL CTA ═══════════════════════════ */}
        <section className="py-20 md:py-28">
          <div className="container max-w-xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Bereit, deine Wallbox für dich arbeiten zu lassen?
              </h2>
              <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-muted-foreground">
                Melde dich jetzt kostenlos an und gehöre zu den Ersten in deiner Region.
                Kein Risiko, keine Kosten, jederzeit kündbar.
              </p>
              <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
                <Link to="/host-signup">
                  <Button size="lg" className="gap-2 rounded-full px-8 text-base font-semibold shadow-lg shadow-primary/25">
                    <Sparkles className="h-5 w-5" />
                    Jetzt kostenlos starten
                  </Button>
                </Link>
                <Link to="/driver-waitlist">
                  <Button size="lg" variant="ghost" className="gap-2 text-base text-muted-foreground">
                    Ich suche eine Ladestation
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
              <p className="mt-5 text-xs text-muted-foreground">
                ✓ Kostenlos  ✓ Kein Gewerbe  ✓ In 2 Minuten fertig
              </p>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/50 bg-muted/20 py-8">
        <div className="container flex flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <img src={chargeflipLogo} alt="chargeflip" className="h-4 w-auto" />
            <span>© 2026 chargeflip</span>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
            <Link to="/host-signup" className="transition-colors hover:text-foreground">Geld verdienen</Link>
            <Link to="/driver-waitlist" className="transition-colors hover:text-foreground">Ladestation finden</Link>
            <Link to="/impressum" className="transition-colors hover:text-foreground">Impressum</Link>
            <Link to="/agb" className="transition-colors hover:text-foreground">AGB</Link>
            <Link to="/datenschutz" className="transition-colors hover:text-foreground">Datenschutz</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
